// Timed keyframes for the MiniMax H3 page's start/end-frames mode: a list of
// interior stills, each pinned to a time (seconds) inside the clip. The
// worker rounds each time to a 24 fps frame on the clip's 17n+5 grid; rows
// that land outside the clip or on another row's frame are flagged inline
// (lib/h3.ts keyframeTimeIssues) and block Generate.

import { Alert, Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/AddRounded';
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded';
import { useTranslation, format } from '../../i18n';
import { AssetInput } from './AssetInput';
import type { UploadResult } from './FrameInput';
import {
  H3_KEYFRAME_TIME_STEP,
  H3_RECOMMENDED_KEYFRAMES,
  h3LastFrameTime,
  keyframeTimeIssues,
  nextKeyframeTime,
  type H3KeyframeIssue,
} from '../../lib/h3';

export type KeyframeSlot = { id: number; image: UploadResult | null; time: string };

let nextId = 1;
export function newKeyframeSlot(time: number, image: UploadResult | null = null): KeyframeSlot {
  return { id: nextId++, image, time: String(time) };
}

export function parseKeyframeTime(s: string): number | null {
  if (!s.trim()) return null;
  const v = Number(s);
  return Number.isFinite(v) ? v : null;
}

// Issues per row, image presence included. Null = the row is fine.
export function keyframeSlotIssues(slots: KeyframeSlot[], duration: number): (H3KeyframeIssue | { kind: 'image' } | null)[] {
  const timeIssues = keyframeTimeIssues(slots.map((s) => parseKeyframeTime(s.time)), duration);
  return slots.map((s, i) => timeIssues[i] ?? (s.image ? null : { kind: 'image' }));
}

export function KeyframesInput({
  uploadUrl,
  value,
  onChange,
  duration,
  max,
  onError,
}: {
  uploadUrl: string;
  value: KeyframeSlot[];
  onChange: (next: KeyframeSlot[]) => void;
  duration: number;
  // How many interior keyframes still fit (8 anchors minus first/last).
  max: number;
  onError?: (msg: string) => void;
}) {
  const h = useTranslation().minimaxH3;
  const issues = keyframeSlotIssues(value, duration);
  const maxTime = String(Math.round(h3LastFrameTime(duration) * 1000) / 1000);

  const add = () => {
    const times = value.map((s) => parseKeyframeTime(s.time)).filter((t): t is number => t != null);
    onChange([...value, newKeyframeSlot(nextKeyframeTime(times, duration))]);
  };
  const update = (id: number, patch: Partial<KeyframeSlot>) => onChange(value.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const remove = (id: number) => onChange(value.filter((s) => s.id !== id));

  const message = (issue: ReturnType<typeof keyframeSlotIssues>[number]): string | null => {
    if (!issue) return null;
    switch (issue.kind) {
      case 'time':
        return h.keyframeBadTime;
      case 'outside':
        return format(h.keyframeOutside, { max: maxTime });
      case 'same':
        return format(h.keyframeSameFrame, { n: issue.other + 1 });
      case 'image':
        return h.keyframeNeedsImage;
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2">
          {h.keyframes}{' '}
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
            ({h.optional})
          </Box>
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={add} disabled={value.length >= max}>
          {h.addKeyframe}
        </Button>
      </Stack>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: value.length ? 1.5 : 0 }}>
        {h.keyframesHint}
      </Typography>
      {value.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
          {value.map((slot, i) => {
            const label = format(h.keyframeLabel, { n: i + 1 });
            const issue = issues[i];
            const timeError = issue && issue.kind !== 'image' ? message(issue) : null;
            return (
              <Box key={slot.id} data-testid="keyframe-row" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <AssetInput
                  uploadUrl={uploadUrl}
                  kind="image"
                  label={label}
                  value={slot.image}
                  onChange={(v) => update(slot.id, { image: v })}
                  onClear={() => update(slot.id, { image: null })}
                  onError={onError}
                />
                <Stack direction="row" spacing={0.5} alignItems="flex-start">
                  <TextField
                    size="small"
                    type="number"
                    label={h.keyframeTime}
                    value={slot.time}
                    onChange={(e) => update(slot.id, { time: e.target.value })}
                    error={!!timeError}
                    helperText={timeError ?? (issue?.kind === 'image' ? message(issue) : ' ')}
                    inputProps={{ min: H3_KEYFRAME_TIME_STEP, max: duration, step: H3_KEYFRAME_TIME_STEP, 'aria-label': `${label} ${h.keyframeTime}` }}
                    FormHelperTextProps={{ sx: { mx: 0 } }}
                    sx={{ flex: 1 }}
                  />
                  <Tooltip title={format(h.removeKeyframe, { n: i + 1 })} arrow>
                    <IconButton aria-label={format(h.removeKeyframe, { n: i + 1 })} onClick={() => remove(slot.id)} sx={{ mt: 0.25 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            );
          })}
        </Box>
      )}
      {value.length > H3_RECOMMENDED_KEYFRAMES && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {h.keyframesMany}
        </Alert>
      )}
    </Box>
  );
}
